import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  Download,
  Upload,
  FileSpreadsheet,
  FileDown,
  Users,
  Phone,
  Mail,
  MessageCircle,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  X,
  Plus,
} from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import StarScore from "@/components/crm/StarScore";
import { ScoreChip } from "@/components/crm/ScoreBadge";
import AddLeadDialog from "@/components/crm/AddLeadDialog";
import ImportDialog from "@/components/crm/ImportDialog";
import GoogleSheetsDialog from "@/components/crm/GoogleSheetsDialog";
import { leadsApi, insightsApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import BulkSendDialog from "@/components/crm/BulkSendDialog";

/**
 * v1.9.0 — procedure steps mirror backend `PROCEDURE_KEYS` in server.py.
 * The label is what the user sees, the `due` label describes what the
 * filter targets ("Catalogue à envoyer" = leads dont catalog_sent=false).
 * Keep this list in sync with the backend constant — there's no API
 * endpoint to read it because it's a static enum that ships with the app.
 */
const PROCEDURE_STEPS = [
  { key: "contacted_24h", label: "Premier contact (24h)", due: "Premier contact à faire" },
  { key: "catalog_sent",  label: "Catalogue envoyé",      due: "Catalogue à envoyer" },
  { key: "plans_sent",    label: "Plans envoyés",         due: "Plans à envoyer" },
  { key: "rdv_fixed",     label: "RDV fixé",              due: "RDV à fixer" },
  { key: "rdv_done",      label: "RDV fait",              due: "RDV à faire" },
  { key: "financing_sent", label: "Financement envoyé",   due: "Financement à envoyer" },
  { key: "offer_made",    label: "Offre faite",           due: "Offre à formuler" },
  { key: "closing",       label: "Closing",               due: "Closing à signer" },
];

// Special "Étape à faire" filter (not a procedure flag): leads that NEVER had
// a real first contact — welcome message never sent AND no first inbound
// WhatsApp. Unlike `contacted_24h` (a manual procedure flag), this targets the
// actual contact events, so it surfaces leads the CRM never reached out to.
const NO_FIRST_CONTACT = "__no_first_contact__";
const NO_FIRST_CONTACT_LABEL = "Premier contact jamais reçu";
import {
  statutLabel,
  statutColor,
  prioriteColor,
  bucketOf,
  BUCKET_META,
  formatDate,
  formatFCFA,
  typeAppartLabel,
  localisationLabel,
} from "@/lib/crmHelpers";
import { downloadCSV } from "@/lib/csvExport";
import { cn } from "@/lib/utils";

const cleanPhone = (p = "") => p.replace(/[\s().-]/g, "");

// Strip diacritics (é → e, ï → i, ï‚, etc.) so "Said" matches "Saïd" and vice versa.
const norm = (s = "") =>
  s
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

// True if `q` looks like a phone-number search (mostly digits, with optional + - space).
const looksLikePhone = (q) => /^[+\d][\d\s().-]{2,}$/.test(q.trim());

// Estimate a numeric budget for a lead, preferring `montant_prevu` (numeric FCFA),
// falling back to parsing "60M - 80M FCFA" / "100M" / "75 millions" patterns.
function estimateBudgetFCFA(lead) {
  const m = Number(lead?.montant_prevu);
  if (m && m > 0) return m;
  const txt = (lead?.budget || "").toLowerCase();
  if (!txt.trim()) return 0;
  // Match "60M", "60 m", "60 millions", "60 m fcfa"
  const matches = [...txt.matchAll(/(\d+(?:[.,]\d+)?)\s*(m|milli|millions?)/gi)];
  if (matches.length === 0) return 0;
  const nums = matches.map((mm) => parseFloat(mm[1].replace(",", ".")) * 1_000_000);
  // For a range like "60M - 80M" we keep the upper bound (max budget).
  return Math.max(...nums);
}

// Buckets for the budget filter — rounded to common SOGI brackets in FCFA.
const BUDGET_BRACKETS = [
  { value: "all",     label: "Tous budgets",        test: () => true },
  { value: "none",    label: "Sans budget",         test: (b) => b === 0 },
  { value: "lt30m",   label: "< 30M FCFA",          test: (b) => b > 0 && b < 30_000_000 },
  { value: "30-60m",  label: "30 – 60M FCFA",       test: (b) => b >= 30_000_000 && b < 60_000_000 },
  { value: "60-100m", label: "60 – 100M FCFA",      test: (b) => b >= 60_000_000 && b < 100_000_000 },
  { value: "100-200m",label: "100 – 200M FCFA",     test: (b) => b >= 100_000_000 && b < 200_000_000 },
  { value: "gt200m",  label: "> 200M FCFA (premium)", test: (b) => b >= 200_000_000 },
];

const budgetMatches = (lead, value) => {
  if (value === "all") return true;
  const bracket = BUDGET_BRACKETS.find((b) => b.value === value);
  if (!bracket) return true;
  return bracket.test(estimateBudgetFCFA(lead));
};

const COLUMNS = [
  { key: "nom",                  label: "Nom",         sortable: true,  className: "min-w-[180px]" },
  { key: "statut",               label: "Statut",      sortable: true,  className: "w-[120px]" },
  { key: "priorite",             label: "Priorité",    sortable: true,  className: "w-[100px]" },
  { key: "score",                label: "Étoiles",     sortable: true,  align: "center", className: "w-[110px]" },
  { key: "quality_score",        label: "Score qualité", sortable: true, align: "center", className: "w-[110px]" },
  { key: "probabilite",          label: "Proba.",      sortable: true,  align: "center", className: "w-[80px]" },
  { key: "programme_immobilier", label: "Programme",   sortable: true,  className: "w-[160px]" },
  { key: "commercial",           label: "Commercial",  sortable: true,  className: "w-[140px]" },
  { key: "budget",               label: "Budget",      sortable: true,  className: "w-[140px]" },
  { key: "derniere_action",      label: "Dernier contact", sortable: true, className: "w-[130px]" },
  { key: "prochaine_action",     label: "Relance",     sortable: true,  className: "w-[120px]" },
];

export default function LeadsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeProjectId, activeProject } = useSettings();
  const { user } = useAuth();
  // v1.8.2 — le filtre "Programme" est admin uniquement. Pour les
  // commerciaux le scope est déjà figé par leur projet actif (cookie
  // current_projet + assigned_projets), donc proposer un dropdown
  // "Programme" en plus est redondant et dangereux (peut donner
  // l'illusion qu'on peut basculer entre projets).
  const isAdmin = user?.role === "admin";

  const [leads, setLeads] = useState([]);
  const [insightsMap, setInsightsMap] = useState({});
  const [loading, setLoading] = useState(true);
  // Auto-open the AddLeadDialog when the URL contains ?new=1.
  // This is the deep-link entry-point so /leads/new (which redirects here)
  // and /leads?new=1 both land on the create form.
  const [addOpen, setAddOpen] = useState(searchParams.get("new") === "1");
  const [importOpen, setImportOpen] = useState(false);
  const [googleOpen, setGoogleOpen] = useState(false);

  // Filters from URL (6 critères : recherche libre + classement + programme +
  // commercial + budget + étape procédure).
  // v1.9.0 — `etapeFilter` cible les leads dont l'étape sélectionnée n'est
  // PAS encore cochée → utile pour faire une relance ciblée ("tous les
  // leads à qui je n'ai pas encore envoyé le catalogue").
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [bucketFilter, setBucketFilter] = useState(searchParams.get("bucket") || "all");
  const [programmeFilter, setProgrammeFilter] = useState(searchParams.get("programme") || "all");
  const [commercialFilter, setCommercialFilter] = useState(searchParams.get("commercial") || "all");
  const [budgetFilter, setBudgetFilter] = useState(searchParams.get("budget") || "all");
  const [etapeFilter, setEtapeFilter] = useState(searchParams.get("etape") || "all");

  // v1.9.0 — multi-select state for bulk WhatsApp send. `selectedIds` is a
  // Set of lead.id strings. We use a Set so add/remove are O(1) and the
  // "select all" toggle stays cheap on 1000-lead pages.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkSendOpen, setBulkSendOpen] = useState(false);

  // v2.2.0 — "Mes leads" toggle. Pour un commercial : restreint la liste
  // aux leads dont assigned_to == son user_id. Persiste dans l'URL.
  const [mineOnly, setMineOnly] = useState(searchParams.get("mine") === "1");

  const [sortKey, setSortKey] = useState(searchParams.get("sortBy") || "derniere_action");
  const [sortDir, setSortDir] = useState(searchParams.get("sortDir") || "desc");

  // Sync filters → URL (so they're shareable / preserved across reloads)
  useEffect(() => {
    const next = new URLSearchParams();
    if (search) next.set("q", search);
    if (bucketFilter !== "all") next.set("bucket", bucketFilter);
    if (programmeFilter !== "all") next.set("programme", programmeFilter);
    if (commercialFilter !== "all") next.set("commercial", commercialFilter);
    if (budgetFilter !== "all") next.set("budget", budgetFilter);
    if (etapeFilter !== "all") next.set("etape", etapeFilter);
    if (sortKey !== "derniere_action") next.set("sortBy", sortKey);
    if (sortDir !== "desc") next.set("sortDir", sortDir);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, bucketFilter, programmeFilter, commercialFilter, budgetFilter, etapeFilter, sortKey, sortDir]);

  // Re-sync state when URL params change externally (e.g., user clicks a bucket
  // chip on the dashboard, or a programme card on /programmes, while LeadsListPage
  // is already mounted via cached navigation).
  const urlBucket = searchParams.get("bucket");
  const urlProgramme = searchParams.get("programme");
  const urlCommercial = searchParams.get("commercial");
  const urlBudget = searchParams.get("budget");
  const urlEtape = searchParams.get("etape");
  const urlQ = searchParams.get("q");
  useEffect(() => {
    setBucketFilter(urlBucket || "all");
    setProgrammeFilter(urlProgramme || "all");
    setCommercialFilter(urlCommercial || "all");
    setBudgetFilter(urlBudget || "all");
    setEtapeFilter(urlEtape || "all");
    setSearch(urlQ || "");
  }, [urlBucket, urlProgramme, urlCommercial, urlBudget, urlEtape, urlQ]);

  // v2.4.0 — Pagination "Charger plus". On garde le filtrage client-side
  // sur le subset déjà chargé (pour ne pas casser l'UX des 6 filtres),
  // mais on ne tire que `PAGE_SIZE` leads à la fois. Pratique sur 5000+.
  const PAGE_SIZE = 200;
  const [hasMore, setHasMore] = useState(false);
  const [totalLeads, setTotalLeads] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [page, i] = await Promise.all([
        leadsApi.page(activeProjectId, { mine: mineOnly, limit: PAGE_SIZE, offset: 0 }),
        insightsApi.forProject(activeProjectId).catch(() => ({})),
      ]);
      setLeads(Array.isArray(page?.items) ? page.items : []);
      setTotalLeads(page?.total || 0);
      setHasMore(!!page?.has_more);
      setInsightsMap(i && typeof i === "object" ? i : {});
    } catch (e) {
      // global axios interceptor handles it
    } finally {
      setLoading(false);
    }
  }, [activeProjectId, mineOnly]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const page = await leadsApi.page(activeProjectId, {
        mine: mineOnly,
        limit: PAGE_SIZE,
        offset: leads.length,
      });
      const items = Array.isArray(page?.items) ? page.items : [];
      setLeads((prev) => [...prev, ...items]);
      setHasMore(!!page?.has_more);
      setTotalLeads(page?.total || totalLeads);
    } catch (e) {
      // silent
    } finally {
      setLoadingMore(false);
    }
  }, [activeProjectId, mineOnly, leads.length, hasMore, loadingMore, totalLeads]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Build dynamic filter options from existing data
  const programmeOptions = useMemo(() => {
    const set = new Set();
    leads.forEach((l) => l.programme_immobilier && set.add(l.programme_immobilier));
    return [...set].sort();
  }, [leads]);

  const commercialOptions = useMemo(() => {
    const set = new Set();
    leads.forEach((l) => l.commercial && set.add(l.commercial));
    return [...set].sort();
  }, [leads]);

  // Apply 5 filters: search (nom/tel/email/etc), classement, programme, commercial, budget
  const filteredLeads = useMemo(() => {
    const rawQ = search.trim();
    const q = norm(rawQ);                            // diacritics-insensitive
    const phoneQ = cleanPhone(rawQ);                 // digits-only
    const isPhoneQuery = looksLikePhone(rawQ);

    return leads.filter((l) => {
      // 1. Recherche libre — nom, téléphone, email, ville, programme, commercial, source, notes
      if (q) {
        const phone = cleanPhone(l.telephone || "");
        const phoneMatches = phoneQ.length >= 3 && phone.includes(phoneQ);
        if (isPhoneQuery && phoneMatches) {
          // OK, on continue
        } else {
          const hay = norm(
            [
              l.nom,
              l.telephone,
              l.email,
              l.programme_immobilier,
              l.commercial,
              l.source,
              l.budget,
              l.notes,
              l.localisation,
              l.localisation === "dakar" ? "Dakar" : "",
              l.localisation === "diaspora" ? "Diaspora" : "",
              l.ville,
              l.pays,
              l.type_appart,
            ]
              .filter(Boolean)
              .join(" ")
          );
          if (!hay.includes(q) && !phoneMatches) return false;
        }
      }
      // 2. Classement
      if (bucketFilter !== "all" && bucketOf(l) !== bucketFilter) return false;
      // 3. Programme
      if (programmeFilter !== "all" && l.programme_immobilier !== programmeFilter) return false;
      // 4. Commercial
      if (commercialFilter !== "all" && l.commercial !== commercialFilter) return false;
      // 5. Budget (tranches FCFA)
      if (!budgetMatches(l, budgetFilter)) return false;
      // 6. Étape procédure (v1.9.0 → v1.9.1 → v2.8.0 → v2.8.1).
      //
      // Un lead appartient à UN SEUL bucket = son "next-step", la prochaine
      // étape concrète à accomplir. Définition : l'étape la plus précoce
      // qui (a) n'est pas faite, ET (b) n'a aucune étape ultérieure faite.
      //
      // Exemples :
      //   {tout false}                          → next = contacted_24h
      //   {contacted:T, catalog:T}              → next = plans_sent
      //   {contacted:T, plans:T} (catalog oublié) → next = rdv_fixed
      //                                            (catalog est sauté car
      //                                             plans, plus tard, est T)
      //   {closing:T}                           → next = null (tout fini)
      //
      // Ainsi le filtre est mutually exclusive (un lead = un seul filtre)
      // et tolère les workflows non-linéaires (commercial qui saute des
      // cases). Historique des tentatives :
      //   v1.9.0 : `procedure[key] !== true` → tous les leads neufs
      //            matchaient chaque filtre. Pas utilisable.
      //   v1.9.1 : strict next-step (premier !== true dans l'ordre)
      //            → un lead avec plans:T mais catalog:F restait
      //            bloqué sur "Catalogue à envoyer" → confusion.
      //   v2.8.0 : "step pas faite ET aucune ultérieure faite" → revenait
      //            au problème v1.9.0 (tous les leads neufs matchaient
      //            n'importe quel filtre "à faire" en amont).
      //   v2.8.1 : next-step skipping → mutually exclusive ET tolérant.
      if (etapeFilter !== "all") {
        if (l.statut === "perdu" || l.statut === "gagne") return false;
        // Special filter: leads that never had a first contact. Based on the
        // REAL events (welcome actually sent, or a first inbound WhatsApp),
        // not the manual `procedure.contacted_24h` flag. "#claimed" is an
        // in-flight marker, not a confirmed send, so it doesn't count.
        if (etapeFilter === NO_FIRST_CONTACT) {
          const ws = l.welcome_sent_at;
          const welcomeReallySent = ws && !String(ws).endsWith("#claimed");
          return !(welcomeReallySent || l.premier_contact_whatsapp);
        }
        const procedure = l.procedure || {};
        // Trouve le next-step réel : première étape non-faite ET sans
        // étape ultérieure faite.
        let nextStep = null;
        for (let i = 0; i < PROCEDURE_STEPS.length; i++) {
          const k = PROCEDURE_STEPS[i].key;
          if (procedure[k] === true) continue;
          // Cette étape n'est pas faite. Est-ce qu'une étape plus tard l'est ?
          const laterDone = PROCEDURE_STEPS
            .slice(i + 1)
            .some((s) => procedure[s.key] === true);
          if (laterDone) continue; // sautée, étape implicitement faite
          nextStep = k;
          break;
        }
        if (nextStep !== etapeFilter) return false;
      }
      return true;
    });
  }, [leads, search, bucketFilter, programmeFilter, commercialFilter, budgetFilter, etapeFilter]);

  // Sort
  const sortedLeads = useMemo(() => {
    const copy = [...filteredLeads];
    const get = (l, key) => {
      if (key === "quality_score") return insightsMap[l.id]?.quality_score ?? -1;
      if (key === "probabilite") return insightsMap[l.id]?.probability ?? l.probabilite ?? 0;
      return l[key];
    };
    copy.sort((a, b) => {
      const va = get(a, sortKey);
      const vb = get(b, sortKey);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      let cmp = 0;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), "fr", { sensitivity: "base", numeric: true });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filteredLeads, sortKey, sortDir, insightsMap]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const resetAll = () => {
    setSearch("");
    setBucketFilter("all");
    setProgrammeFilter("all");
    setCommercialFilter("all");
    setBudgetFilter("all");
    setEtapeFilter("all");
  };

  const hasActiveFilter =
    !!search ||
    bucketFilter !== "all" ||
    programmeFilter !== "all" ||
    commercialFilter !== "all" ||
    budgetFilter !== "all" ||
    etapeFilter !== "all";

  // v1.9.0 — multi-select helpers. `toggle` flips one id; `toggleAllVisible`
  // adds every lead in `sortedLeads` to the set (or clears them all when
  // they're already selected). We re-compute `allVisibleSelected` from
  // sortedLeads (not from the full leads list) so the "select all" checkbox
  // reflects the current filtered view rather than the whole DB.
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allVisibleSelected =
    sortedLeads.length > 0 && sortedLeads.every((l) => selectedIds.has(l.id));
  const someVisibleSelected =
    !allVisibleSelected && sortedLeads.some((l) => selectedIds.has(l.id));
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        sortedLeads.forEach((l) => next.delete(l.id));
      } else {
        sortedLeads.forEach((l) => next.add(l.id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // The bulk-send dialog operates only on leads we can actually message,
  // i.e. those with a non-empty `telephone`. We pass this filtered subset
  // down so the dialog can show the right count + skip silently when an
  // entry has no phone.
  const selectedLeads = useMemo(
    () => leads.filter((l) => selectedIds.has(l.id) && l.telephone),
    [leads, selectedIds]
  );

  const handleExport = () => {
    downloadCSV(sortedLeads, `leads_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <AppShell
      topbarProps={{
        search,
        onSearchChange: setSearch,
        onAddLead: () => setAddOpen(true),
        onRefresh: loadAll,
      }}
    >
      <div className="mx-auto max-w-[1400px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        {/* Page header */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-label text-muted-foreground">Pipeline</p>
            <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
              Leads
            </h1>
            <p className="text-sm text-muted-foreground">
              {activeProject?.nom
                ? `${leads.length} lead${leads.length > 1 ? "s" : ""} · ${activeProject.nom}`
                : `${leads.length} lead${leads.length > 1 ? "s" : ""} tous projets`}
              {hasActiveFilter && (
                <span className="ml-2 text-foreground">
                  · <strong>{sortedLeads.length}</strong> après filtre{sortedLeads.length > 1 ? "s" : ""}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Données dropdown — same options as the Dashboard topbar so the
                user has Import / Google Sheets / Export available everywhere. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="leads-data-menu-btn"
                  className="gap-2"
                >
                  <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                  Données
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[220px]">
                <DropdownMenuItem
                  onClick={() => setImportOpen(true)}
                  data-testid="leads-menu-import-csv"
                  className="gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Importer un CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setGoogleOpen(true)}
                  data-testid="leads-menu-google-sheets"
                  className="gap-2"
                >
                  <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                  Google Sheets
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleExport}
                  disabled={sortedLeads.length === 0}
                  data-testid="leads-menu-export-csv"
                  className="gap-2"
                >
                  <FileDown className="h-4 w-4" />
                  Exporter le CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* v2.2.0 — Toggle "Mes leads" : filtre les leads assignés
                au user connecté. Utile pour les commerciaux qui veulent
                voir uniquement leur pipeline personnel. Backend reçoit
                `?mine=true`. Persiste dans l'URL via le sync useEffect. */}
            <Button
              variant={mineOnly ? "default" : "outline"}
              onClick={() => {
                const next = !mineOnly;
                setMineOnly(next);
                const np = new URLSearchParams(searchParams);
                if (next) np.set("mine", "1"); else np.delete("mine");
                setSearchParams(np, { replace: true });
              }}
              data-testid="leads-mine-toggle"
              className={`gap-2 ${
                mineOnly
                  ? "bg-violet-600 text-white hover:bg-violet-700"
                  : ""
              }`}
              title="Afficher uniquement les leads qui me sont attribués"
            >
              <Users className="h-4 w-4" />
              {mineOnly ? "Mes leads ✓" : "Mes leads"}
            </Button>
            <Button
              onClick={() => setAddOpen(true)}
              data-testid="leads-add-btn"
              className="gap-2 bg-accent-navy text-white hover:bg-accent-navy/90 dark:bg-primary dark:text-primary-foreground"
            >
              <Plus className="h-4 w-4" />
              Nouveau lead
            </Button>
          </div>
        </div>

        {/* Filters bar */}
        <div className="mb-4 rounded-2xl border border-border/60 bg-card p-4 shadow-soft">
          {/* Row 1: search */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom, téléphone, email, ville, programme, commercial…"
                data-testid="leads-search-input"
                className="h-10 pl-10 pr-10"
                aria-describedby="leads-search-hint"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Effacer la recherche"
                  data-testid="leads-search-clear"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {hasActiveFilter && (
              <Button
                variant="ghost"
                onClick={resetAll}
                data-testid="leads-reset-filters"
                className="gap-1.5 text-muted-foreground"
              >
                <X className="h-3.5 w-3.5" />
                Réinitialiser ({hasActiveFilter ? "filtres actifs" : ""})
              </Button>
            )}
          </div>

          {/* Row 2: filter selects (4 critères) */}
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <FilterSelect label="Classement" value={bucketFilter} onChange={setBucketFilter}
              options={[
                { value: "all", label: "Tous classements" },
                { value: "hot", label: "🔥 HOT" },
                { value: "warm", label: "🟡 WARM" },
                { value: "nurture", label: "🌱 NURTURE" },
                { value: "pending", label: "🔵 EN ATTENTE" },
                { value: "won", label: "🏆 GAGNÉ" },
                { value: "lost", label: "⊘ PERDU" },
              ]} testId="filter-bucket" />

            {/* Filtre Programme — admin uniquement (v1.8.2). Pour les
                commerciaux, le scope projet vient du cookie + assigned
                _projets, pas besoin d'un dropdown qui doublonne. */}
            {isAdmin && (
              <FilterSelect label="Programme" value={programmeFilter} onChange={setProgrammeFilter}
                options={[
                  { value: "all", label: "Tous programmes" },
                  ...programmeOptions.map((p) => ({ value: p, label: p })),
                ]} testId="filter-programme" disabled={programmeOptions.length === 0} />
            )}

            <FilterSelect label="Commercial" value={commercialFilter} onChange={setCommercialFilter}
              options={[
                { value: "all", label: "Tous commerciaux" },
                ...commercialOptions.map((c) => ({ value: c, label: c })),
              ]} testId="filter-commercial" disabled={commercialOptions.length === 0} />

            <FilterSelect label="Budget" value={budgetFilter} onChange={setBudgetFilter}
              options={BUDGET_BRACKETS.map((b) => ({ value: b.value, label: b.label }))}
              testId="filter-budget" />

            {/* v1.9.0 — Étape procédure. Cible les leads dont l'étape
                sélectionnée n'est PAS encore faite — parfait pour relances
                ciblées genre "qui n'a pas encore reçu le catalogue". */}
            <FilterSelect label="Étape à faire" value={etapeFilter} onChange={setEtapeFilter}
              options={[
                { value: "all", label: "Toutes étapes" },
                { value: NO_FIRST_CONTACT, label: NO_FIRST_CONTACT_LABEL },
                ...PROCEDURE_STEPS.map((s) => ({ value: s.key, label: s.due })),
              ]} testId="filter-etape" />
          </div>

          {/* Active filter chips */}
          {hasActiveFilter && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Filtres actifs :</span>
              {search && <FilterChip label={`"${search}"`} onClear={() => setSearch("")} />}
              {bucketFilter !== "all" && (
                <FilterChip
                  label={`Classement ${BUCKET_META[bucketFilter]?.label || bucketFilter}`}
                  onClear={() => setBucketFilter("all")}
                />
              )}
              {/* Programme chip — admin only (v1.8.2) */}
              {isAdmin && programmeFilter !== "all" && (
                <FilterChip label={programmeFilter} onClear={() => setProgrammeFilter("all")} />
              )}
              {commercialFilter !== "all" && (
                <FilterChip label={`Comm. ${commercialFilter}`} onClear={() => setCommercialFilter("all")} />
              )}
              {budgetFilter !== "all" && (
                <FilterChip
                  label={BUDGET_BRACKETS.find((b) => b.value === budgetFilter)?.label || budgetFilter}
                  onClear={() => setBudgetFilter("all")}
                />
              )}
              {/* Procedure step chip (v1.9.0) */}
              {etapeFilter !== "all" && (
                <FilterChip
                  label={
                    etapeFilter === NO_FIRST_CONTACT
                      ? NO_FIRST_CONTACT_LABEL
                      : PROCEDURE_STEPS.find((s) => s.key === etapeFilter)?.due ||
                        etapeFilter
                  }
                  onClear={() => setEtapeFilter("all")}
                />
              )}
            </div>
          )}
        </div>

        {/* v1.9.0 — Bulk-send action bar. Appears when ≥1 lead is selected.
            Sticky-ish (just sits above the table) to stay visible while
            scrolling through the list. Hidden when nothing's selected so
            it doesn't take up vertical space for users who never use it. */}
        {selectedIds.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-300/60 bg-emerald-50 px-4 py-2.5 shadow-soft dark:border-emerald-900/40 dark:bg-emerald-950/30">
            <div className="text-sm">
              <span className="font-bold text-emerald-700 dark:text-emerald-300">
                {selectedIds.size} lead{selectedIds.size > 1 ? "s" : ""} sélectionné
                {selectedIds.size > 1 ? "s" : ""}
              </span>
              {selectedLeads.length !== selectedIds.size && (
                <span className="ml-2 text-xs text-amber-700">
                  ({selectedIds.size - selectedLeads.length} sans téléphone ·{" "}
                  ignoré{selectedIds.size - selectedLeads.length > 1 ? "s" : ""})
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={clearSelection}
                className="gap-1.5"
                data-testid="leads-bulk-clear"
              >
                <X className="h-3.5 w-3.5" />
                Désélectionner
              </Button>
              <Button
                size="sm"
                onClick={() => setBulkSendOpen(true)}
                disabled={selectedLeads.length === 0}
                className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="leads-bulk-send"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                Envoyer WhatsApp à {selectedLeads.length} lead
                {selectedLeads.length > 1 ? "s" : ""}
              </Button>
            </div>
          </div>
        )}

        {/* Table (desktop) */}
        <div className="hidden md:block">
          <div className="scrollbar-thin overflow-x-auto rounded-2xl border border-border/60 bg-card shadow-soft">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/40 backdrop-blur">
                <tr className="border-b border-border/60">
                  {/* v1.9.0 — checkbox column for multi-select bulk send.
                      The header checkbox toggles every lead in the current
                      filtered view; clicking a row's checkbox stops the
                      row's onClick so we don't navigate to the lead. */}
                  <th className="w-10 px-3 py-2.5 text-center">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someVisibleSelected;
                      }}
                      onChange={toggleAllVisible}
                      className="h-4 w-4 cursor-pointer rounded border-border accent-emerald-600"
                      title={
                        allVisibleSelected
                          ? "Tout désélectionner"
                          : "Sélectionner tous les leads visibles"
                      }
                      data-testid="leads-select-all"
                    />
                  </th>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      onClick={() => c.sortable && toggleSort(c.key)}
                      className={cn(
                        "px-3 py-2.5 text-left text-label font-semibold text-muted-foreground",
                        c.align === "center" && "text-center",
                        c.sortable && "cursor-pointer hover:text-foreground select-none",
                        c.className
                      )}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {c.sortable && (
                          sortKey === c.key ? (
                            sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3 opacity-30" />
                          )
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-border/40">
                      <td className="px-3 py-3" />
                      {COLUMNS.map((c) => (
                        <td key={c.key} className="px-3 py-3">
                          <span className="block h-4 w-3/4 animate-shimmer rounded" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : sortedLeads.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} className="px-4 py-12 text-center">
                      <Users className="mx-auto h-8 w-8 text-muted-foreground/40" />
                      <p className="mt-3 font-heading text-sm font-semibold">
                        {hasActiveFilter ? "Aucun lead ne correspond à vos filtres" : "Aucun lead pour le moment"}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {hasActiveFilter ? "Essayez d'élargir vos critères ou réinitialisez les filtres." : "Importez un CSV ou créez votre premier lead."}
                      </p>
                      {hasActiveFilter && (
                        <Button variant="link" onClick={resetAll} className="mt-2 h-auto p-0 text-xs">
                          Réinitialiser les filtres
                        </Button>
                      )}
                    </td>
                  </tr>
                ) : (
                  sortedLeads.map((lead) => {
                    const ins = insightsMap[lead.id];
                    const proba = ins?.probability ?? lead.probabilite ?? 0;
                    const b = BUCKET_META[bucketOf(lead)];
                    return (
                      <tr
                        key={lead.id}
                        data-testid={`leads-row-${lead.id}`}
                        onClick={() => navigate(`/leads/${lead.id}`)}
                        className={cn(
                          "cursor-pointer border-b border-border/40 transition-colors hover:bg-muted/40",
                          selectedIds.has(lead.id) && "bg-emerald-50/40 dark:bg-emerald-950/20"
                        )}
                      >
                        {/* v1.9.0 — per-row select checkbox. stopPropagation
                            so the row's navigate handler doesn't fire when
                            the user is just checking the box. */}
                        <td
                          className="w-10 px-3 py-2.5 text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.has(lead.id)}
                            onChange={() => toggleSelect(lead.id)}
                            className="h-4 w-4 cursor-pointer rounded border-border accent-emerald-600"
                            data-testid={`leads-select-${lead.id}`}
                          />
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="font-heading font-semibold">{lead.nom}</div>
                            <span className={cn("rounded border px-1 py-0 text-[9px] font-bold uppercase tracking-wider", b.color)}>
                              {b.label}
                            </span>
                          </div>
                          {/* Phone + email on SEPARATE lines + nowrap on the
                              phone digits so "+221 77 ..." never wraps mid-
                              number (was breaking into 2 lines when the
                              column got tight). Email stays truncated. */}
                          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <Phone className="h-3 w-3 flex-none" />
                            <span className="whitespace-nowrap font-mono tabular-nums">
                              {lead.telephone}
                            </span>
                          </div>
                          {lead.email && (
                            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                              <Mail className="h-3 w-3 flex-none" />
                              <span className="truncate">
                                {lead.email.length > 28 ? `${lead.email.slice(0, 28)}…` : lead.email}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={cn("rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider", statutColor(lead.statut))}>
                            {statutLabel(lead.statut)}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={cn("rounded-md border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider", prioriteColor(lead.priorite))}>
                            {lead.priorite}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <StarScore value={lead.score || 0} readOnly size="sm" />
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {/* Closed leads (gagne/perdu) → score not actionable.
                              Hide it to avoid the "perdu lead with 47/100" confusion. */}
                          {(lead.statut === "gagne" || lead.statut === "perdu") ? (
                            <span className="text-muted-foreground" title="Lead clos">—</span>
                          ) : ins?.quality_score != null ? (
                            <ScoreChip score={ins.quality_score} />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-center text-xs font-bold tabular-nums">
                          {(lead.statut === "gagne" || lead.statut === "perdu") ? (
                            <span className="text-muted-foreground" title="Lead clos">—</span>
                          ) : proba > 0 ? `${proba}%` : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="text-xs">
                            {lead.programme_immobilier || <span className="text-muted-foreground">—</span>}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {typeAppartLabel(lead.type_appart).toUpperCase()} · {localisationLabel(lead.localisation)}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-xs">
                          {lead.commercial || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-3 py-2.5 text-xs font-medium text-accent-gold">
                          {lead.budget || (
                            lead.montant_prevu
                              ? formatFCFA(lead.montant_prevu)
                              : <span className="font-normal text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground">
                          {lead.derniere_action ? formatDate(lead.derniere_action) : "—"}
                        </td>
                        <td className="px-3 py-2.5 text-xs">
                          {lead.prochaine_action ? (
                            <span className={cn(
                              "inline-flex items-center gap-1 rounded px-1.5 py-0.5",
                              lead.prochaine_action.slice(0, 10) < new Date().toISOString().slice(0, 10)
                                ? "bg-red-50 font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300"
                                : "text-muted-foreground"
                            )}>
                              {formatDate(lead.prochaine_action)}
                            </span>
                          ) : "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="space-y-2 md:hidden">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 animate-shimmer rounded-xl border border-border/60" />
            ))
          ) : sortedLeads.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-border/60 p-8 text-center">
              <Users className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-semibold">
                {hasActiveFilter ? "Aucun lead correspondant" : "Aucun lead"}
              </p>
              {hasActiveFilter && (
                <Button variant="link" onClick={resetAll} className="mt-1 h-auto p-0 text-xs">
                  Réinitialiser les filtres
                </Button>
              )}
            </div>
          ) : (
            sortedLeads.map((lead) => {
              const ins = insightsMap[lead.id];
              const proba = ins?.probability ?? lead.probabilite ?? 0;
              const b = BUCKET_META[bucketOf(lead)];
              return (
                <div
                  key={lead.id}
                  onClick={() => navigate(`/leads/${lead.id}`)}
                  data-testid={`leads-card-${lead.id}`}
                  className="cursor-pointer rounded-xl border border-border/60 bg-card p-3 shadow-soft active:bg-muted/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-heading truncate font-semibold">{lead.nom}</span>
                        <span className={cn("rounded border px-1 py-0 text-[9px] font-bold uppercase tracking-wider", b.color)}>
                          {b.label}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                        <Phone className="h-3 w-3" />
                        {lead.telephone}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={cn("rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase", statutColor(lead.statut))}>
                        {statutLabel(lead.statut)}
                      </span>
                      {/* Hide score on closed leads — see desktop table comment */}
                      {ins?.quality_score != null
                        && lead.statut !== "gagne"
                        && lead.statut !== "perdu"
                        && <ScoreChip score={ins.quality_score} />}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">
                      {lead.programme_immobilier || "Sans programme"}
                    </span>
                    {proba > 0 && <span className="font-bold text-accent-blue">{proba}%</span>}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {!loading && sortedLeads.length > 0 && (
          <p className="mt-4 text-center text-xs text-muted-foreground">
            {sortedLeads.length} résultat{sortedLeads.length > 1 ? "s" : ""}
            {leads.length !== sortedLeads.length && ` sur ${leads.length} chargés`}
            {totalLeads > leads.length && ` · ${totalLeads} en base`}
          </p>
        )}

        {/* v2.4.0 — Bouton "Charger plus" quand has_more = true.
            Garde le scroll position naturel (append, pas remplace). */}
        {!loading && hasMore && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              onClick={loadMore}
              disabled={loadingMore}
              className="gap-2"
              data-testid="leads-load-more"
            >
              {loadingMore ? (
                "Chargement…"
              ) : (
                <>
                  Charger plus ·{" "}
                  <span className="text-muted-foreground">
                    {totalLeads - leads.length} restants
                  </span>
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      <AddLeadDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreate={async (payload) => {
          await leadsApi.create(payload);
          await loadAll();
        }}
      />
      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={loadAll}
      />
      <GoogleSheetsDialog
        open={googleOpen}
        onOpenChange={setGoogleOpen}
        onImported={loadAll}
      />
      {/* v1.9.0 — Bulk-send composer. Operates on the filtered subset
          (`selectedLeads` = selected ids ∩ has phone). On success we
          clear the selection so the action bar disappears and the user
          can move on without manually un-checking everything. */}
      <BulkSendDialog
        open={bulkSendOpen}
        onOpenChange={setBulkSendOpen}
        leads={selectedLeads}
        totalSelected={selectedIds.size}
        onSent={(result) => {
          if ((result?.sent || 0) + (result?.deferred || 0) > 0) {
            clearSelection();
          }
        }}
      />
    </AppShell>
  );
}

/* ---------------------------------------------------------------- */

function FilterSelect({ label, value, onChange, options, testId, disabled }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger
          data-testid={testId}
          className={cn("h-9 w-full text-xs", disabled && "opacity-50")}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} className="text-xs">
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function FilterChip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10px] font-medium">
      {label}
      <button
        onClick={onClear}
        className="rounded-full p-0.5 hover:bg-foreground/10"
        aria-label="Retirer le filtre"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}
